import React, { useRef, useState } from 'react';
import {
  Box,
  Chip,
  Popover,
  Stack,
  Paper,
  Typography,
} from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  Language as LanguageIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { SourceReference, WebSourceReference } from '../hooks/useChat';

interface MessageSourceBadgesProps {
  sources: SourceReference[];
  webSources: WebSourceReference[];
}

const deduplicateById = <T extends { id: number | string }>(items: T[]): T[] => {
  const seen = new Map<number | string, T>();
  items.forEach((item) => {
    if (item.id !== undefined && !seen.has(item.id)) {
      seen.set(item.id, item);
    }
  });
  return Array.from(seen.values());
};

const POPOVER_SX = {
  '& .MuiPopover-paper': {
    width: 'min(480px, 90vw)',
    maxHeight: '60vh',
    p: 1.5,
    overflow: 'auto',
  },
};

const MessageSourceBadges: React.FC<MessageSourceBadgesProps> = ({ sources, webSources }) => {
  const dbAnchor = useRef<HTMLDivElement | null>(null);
  const webAnchor = useRef<HTMLDivElement | null>(null);
  const [dbOpen, setDbOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);

  const dbSources = deduplicateById(sources || []);
  const wsSources = deduplicateById(webSources || []);

  if (dbSources.length === 0 && wsSources.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
      {dbSources.length > 0 && (
        <>
          <Box ref={dbAnchor} component="span" sx={{ display: 'inline-flex' }}>
            <Chip
              icon={<MenuBookIcon fontSize="small" />}
              label={`Using GekkoVet sources (${dbSources.length})`}
              size="small"
              clickable
              onClick={() => setDbOpen((v) => !v)}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'primary.contrastText' },
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            />
          </Box>
          <Popover
            open={dbOpen}
            anchorEl={dbAnchor.current}
            onClose={() => setDbOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={POPOVER_SX}
          >
            <Stack spacing={1}>
              {dbSources.map((source, index) => (
                <Paper
                  key={`db-${source.id}`}
                  variant="outlined"
                  sx={{ p: 1.25, backgroundColor: 'grey.50' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '22px',
                        height: '22px',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}
                    >
                      {source.name}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Popover>
        </>
      )}

      {wsSources.length > 0 && (
        <>
          <Box ref={webAnchor} component="span" sx={{ display: 'inline-flex' }}>
            <Chip
              icon={<LanguageIcon fontSize="small" />}
              label={`Using web sources (${wsSources.length})`}
              size="small"
              clickable
              onClick={() => setWebOpen((v) => !v)}
              sx={{
                bgcolor: 'info.main',
                color: 'info.contrastText',
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'info.contrastText' },
                '&:hover': { bgcolor: 'info.dark' },
              }}
            />
          </Box>
          <Popover
            open={webOpen}
            anchorEl={webAnchor.current}
            onClose={() => setWebOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={POPOVER_SX}
          >
            <Stack spacing={1}>
              {wsSources.map((source) => (
                <Paper
                  key={`web-${source.id}`}
                  variant="outlined"
                  onClick={() => window.open(source.url, '_blank', 'noopener,noreferrer')}
                  sx={{
                    p: 1.25,
                    backgroundColor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'grey.100' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '22px',
                        height: '22px',
                        bgcolor: 'info.main',
                        color: 'info.contrastText',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {source.id}
                    </Typography>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {source.title}
                        <LaunchIcon sx={{ fontSize: '0.875rem', opacity: 0.6 }} />
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          wordBreak: 'break-all',
                        }}
                      >
                        {source.url}
                      </Typography>
                      {source.snippet && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            color: 'text.secondary',
                            mt: 0.5,
                          }}
                        >
                          {source.snippet}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default MessageSourceBadges;
